import React, { useCallback, useEffect, useId, useRef, useState } from "react";

interface ScrollableTableWrapperProps {
  children: React.ReactNode;
  className?: string;
  hideNativeScrollbar?: boolean;
  viewportClassName?: string;
  showScrollbar?: boolean;
}

const ScrollableTableWrapper: React.FC<ScrollableTableWrapperProps> = ({
  children,
  className,
  hideNativeScrollbar = true,
  viewportClassName,
  showScrollbar = false,
}) => {
  const viewportId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);
  const [showRightGlow, setShowRightGlow] = useState(false);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbLeft, setThumbLeft] = useState(0);
  const [maxScrollLeft, setMaxScrollLeft] = useState(0);
  const [currentScrollLeft, setCurrentScrollLeft] = useState(0);
  const [isDraggingThumb, setIsDraggingThumb] = useState(false);
  const [floatingTrackFrame, setFloatingTrackFrame] = useState<{ left: number; width: number; visible: boolean }>({
    left: 0,
    width: 0,
    visible: false,
  });

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const trackWidth = trackRef.current?.clientWidth ?? el.clientWidth;
    const maxScrollableLeft = Math.max(el.scrollWidth - el.clientWidth, 0);
    const hasOverflow = maxScrollableLeft > 0;
    const nextThumbWidth = hasOverflow
      ? Math.max(Math.round((el.clientWidth / el.scrollWidth) * trackWidth), 48)
      : trackWidth;
    const maxThumbLeft = Math.max(trackWidth - nextThumbWidth, 0);
    const nextThumbLeft =
      hasOverflow && maxScrollableLeft > 0
        ? Math.round((el.scrollLeft / maxScrollableLeft) * maxThumbLeft)
        : 0;
    const hasMoreRight =
      hasOverflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const clampedLeft = Math.max(Math.round(rect.left), 0);
    const clampedRight = Math.min(Math.round(rect.right), viewportWidth);
    const nextFloatingTrackWidth = Math.max(clampedRight - clampedLeft, 0);
    const isViewportVisible =
      showScrollbar &&
      hasOverflow &&
      rect.bottom > 0 &&
      rect.top < viewportHeight &&
      nextFloatingTrackWidth > 0;

    setHasHorizontalOverflow(hasOverflow);
    setThumbWidth(nextThumbWidth);
    setThumbLeft(nextThumbLeft);
    setMaxScrollLeft(maxScrollableLeft);
    setCurrentScrollLeft(Math.round(el.scrollLeft));
    setShowRightGlow(hasMoreRight);
    setFloatingTrackFrame(
      isViewportVisible
        ? { left: clampedLeft, width: nextFloatingTrackWidth, visible: true }
        : { left: 0, width: 0, visible: false }
    );
  }, [showScrollbar]);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", checkScroll, { passive: true });
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    const contentEl = el.firstElementChild;
    if (contentEl instanceof HTMLElement) {
      observer.observe(contentEl);
    }
    window.addEventListener("resize", checkScroll);
    window.addEventListener("scroll", checkScroll, true);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      observer.disconnect();
      window.removeEventListener("resize", checkScroll);
      window.removeEventListener("scroll", checkScroll, true);
    };
  }, [checkScroll]);

  useEffect(() => {
    if (showScrollbar && hasHorizontalOverflow) {
      checkScroll();
    }
  }, [checkScroll, hasHorizontalOverflow, showScrollbar]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const el = scrollRef.current;
      const dragState = dragStateRef.current;
      if (!el || !dragState || !hasHorizontalOverflow) return;

      const trackWidth = trackRef.current?.clientWidth ?? el.clientWidth;
      const maxThumbTravel = Math.max(trackWidth - thumbWidth, 0);
      const scrollDelta =
        maxThumbTravel > 0
          ? ((event.clientX - dragState.startX) / maxThumbTravel) * maxScrollLeft
          : 0;

      el.scrollLeft = dragState.startScrollLeft + scrollDelta;
      checkScroll();
    };

    const stopDragging = () => {
      dragStateRef.current = null;
      setIsDraggingThumb(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopDragging);
    window.addEventListener("pointercancel", stopDragging);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopDragging);
      window.removeEventListener("pointercancel", stopDragging);
    };
  }, [checkScroll, hasHorizontalOverflow, maxScrollLeft, thumbWidth]);

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !hasHorizontalOverflow) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const trackWidth = event.currentTarget.clientWidth;
    const clickX = event.clientX - rect.left;
    const maxThumbTravel = Math.max(trackWidth - thumbWidth, 0);
    const nextThumbLeft = Math.max(0, Math.min(clickX - thumbWidth / 2, maxThumbTravel));
    const nextScrollLeft =
      maxThumbTravel > 0 ? (nextThumbLeft / maxThumbTravel) * maxScrollLeft : 0;

    el.scrollLeft = nextScrollLeft;
    checkScroll();
  };

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    const el = scrollRef.current;
    if (!el || !hasHorizontalOverflow) return;

    event.preventDefault();
    event.stopPropagation();
    dragStateRef.current = {
      startX: event.clientX,
      startScrollLeft: el.scrollLeft,
    };
    setIsDraggingThumb(true);
  };

  return (
    <div className={`relative ${showScrollbar && hasHorizontalOverflow ? "pb-14" : ""} ${className ?? ""}`}>
      <div
        id={viewportId}
        ref={scrollRef}
        className={`${
          showScrollbar
            ? `overflow-x-auto overscroll-x-contain stw-scroll-viewport ${
                hideNativeScrollbar ? "stw-hide-native-scrollbar" : ""
              }`
            : "overflow-x-auto"
        } ${viewportClassName ?? ""}`}
      >
        {children}
      </div>
      <div
        className={`pointer-events-none absolute top-0 right-0 h-full w-10 bg-gradient-to-l from-amber-500/15 to-transparent transition-opacity duration-300 flex items-center justify-center ${
          showRightGlow ? "opacity-100" : "opacity-0"
        }`}
      >
        <svg
          width="8"
          height="14"
          viewBox="0 0 8 14"
          fill="none"
          className="animate-[nudge_2s_ease-in-out_infinite] opacity-40"
        >
          <path
            d="M1 1L7 7L1 13"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-amber-400"
          />
        </svg>
      </div>
      {showScrollbar && hasHorizontalOverflow && floatingTrackFrame.visible && (
        <div
          className="pointer-events-none fixed bottom-0 z-40 border-t border-white/[0.08] bg-black/95 shadow-[0_-12px_32px_rgba(0,0,0,0.35)]"
          style={{ left: `${floatingTrackFrame.left}px`, width: `${floatingTrackFrame.width}px` }}
        >
          <div
            className="px-3 py-3"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
          >
            <div
              ref={trackRef}
              role="scrollbar"
              aria-label="Horizontal table scrollbar"
              aria-orientation="horizontal"
              aria-controls={viewportId}
              aria-valuemin={0}
              aria-valuemax={maxScrollLeft}
              aria-valuenow={currentScrollLeft}
              className="pointer-events-auto h-2 select-none rounded-full bg-white/10"
              onPointerDown={handleTrackPointerDown}
            >
              <button
                type="button"
                aria-label="Scroll table horizontally"
                className={`block h-full rounded-full bg-white/55 transition-colors ${
                  isDraggingThumb ? "cursor-grabbing bg-white/75" : "cursor-grab hover:bg-white/75"
                }`}
                onPointerDown={handleThumbPointerDown}
                style={{
                  width: `${thumbWidth}px`,
                  transform: `translateX(${thumbLeft}px)`,
                  touchAction: "none",
                }}
              />
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes nudge { 0%,100% { transform: translateX(0); } 50% { transform: translateX(3px); } }
        .stw-hide-native-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .stw-hide-native-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ScrollableTableWrapper;
